import * as React from 'react';
import { observer } from 'mobx-react';
import { IFormProps, IContext } from './Form';

export interface IFieldProps {
    field: {
        onChange<T>(ev: React.FormEvent<T>): void;
        onBlur(fieldName: string): void;
        onFocus(fieldName: string): void;
        onDropDownChange(fieldName: string, value: string): void;
        onPhoneChange(fieldName: string, value: string, ...args: unknown[]): void;
        onResponseChange<T>(ev: React.FormEvent<T>): void;
        name: string;
        id?: string;
        autoFocus?: boolean;
    };
    form: IFormProps;
}

export interface IFieldConfig<CP> {
    component: React.ComponentType<CP & IFieldProps> | React.ComponentType<void>;
    name: string;
    id?: string;
    validations?: {};
    onChange?: Function;
    onFocus?: Function;
    onBlur?: Function;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    afterChange?(fieldName: string, value: any, ...args: unknown[]): void;
    autoFocus?: boolean;
}
@observer
export class Field<CP> extends React.Component<IFieldConfig<CP> & CP> {
    public static contextTypes = {
        form: () => null
    };

    public componentDidUpdate(prevProps: IFieldConfig<CP>) {
        const { form } = this.context as IContext;
        const validations = this.props.validations;
        if (validations && JSON.stringify(validations) !== JSON.stringify(prevProps.validations)) {
            form.setFieldValidations(this.props.name, validations);
        }
    }

    public componentDidMount() {
        const { form } = this.context as IContext;
        const validations = this.props.validations;
        if (validations) {
            form.setFieldValidations(this.props.name, validations);
        }
    }

    public componentWillUnmount() {
        const { form } = this.context as IContext;

        form.removeValidationField(this.props.name);
    }

    public render() {
        const { name, component, ...props } = this.props as IFieldConfig<CP>;
        const id = this.props.id ? this.props.id : name;

        const { form } = this.context as IContext;

        const onChangeWrapper = (ev: React.FormEvent<HTMLInputElement>, ...args: unknown[]) => {
            form.onChange(ev);
            if (this.props.onChange) {
                this.props.onChange(ev);
            }
            if (this.props.afterChange) {
                this.props.afterChange(this.props.name, form.values[this.props.name], ...args);
            }
        };

        const onDropdownChangeWrapper = (fieldName: string, value: string, ...args: unknown[]) => {
            if (form.onDropDownChange) {
                form.onDropDownChange(fieldName, value);
            }
            if (this.props.afterChange) {
                this.props.afterChange(fieldName, value, ...args);
            }
        };

        const onPhoneChangeWrapper = (fieldName: string, value: string, ...args: unknown[]) => {
            if (form.onPhoneInputChange) {
                form.onPhoneInputChange(fieldName, value);
            }
            if (this.props.afterChange) {
                this.props.afterChange(fieldName, value, ...args);
            }
        };

        const onResponseChangeWrapper = (
            ev: React.FormEvent<HTMLInputElement>,
            ...args: unknown[]
        ) => {
            const { onResponseChange, values } = form;
            const { afterChange, name: fieldName } = this.props;

            const value = values[fieldName];

            onResponseChange(ev);

            afterChange?.(fieldName, value, ...args);
        };

        const onBlurWrapper = (fieldName: string) => {
            form.onBlur(fieldName);
            if (this.props.onBlur) {
                this.props.onBlur();
            }
        };

        const field = {
            name,
            id,
            onChange: onChangeWrapper,
            onBlur: onBlurWrapper,
            onFocus: props.onFocus,
            onDropDownChange: onDropdownChangeWrapper,
            onPhoneChange: onPhoneChangeWrapper,
            onResponseChange: onResponseChangeWrapper,
            autoFocus: props.autoFocus
        };
        const bag = { field, form };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return React.createElement(component as any, {
            ...bag,
            ...props,
            name,
            id
        });
    }
}
